<template>
  <div
    :class="[`plan-item--${theme}`, { 'plan-item-selected': selected, 'plan-item--discount': isSuperDisc }]"
    class="plan-item"
  >
    <div
      v-if="isMostPopular"
      :class="[theme]"
      class="most-popular"
    >
      <p class="most-popular__text paragraph-12-700 text-center">
        {{ $t('lp_v7_plan_1month_label') }}
      </p>
    </div>

    <nuxt-icon
      v-if="!selected"
      filled
      name="elipse-empty"
    />
    <nuxt-icon
      v-else
      class="plan-item__check-icon"
      filled
      name="elipse-fill"
    />

    <div class="text-wrapper">
      <p :class="['plan-name paragraph-16-600', { 'plan-name__small': locale !== 'en' }, locale]">
        {{ planName }}
      </p>
      <p class="plan-price">
      </p>
      <p class="paragraph-10-300 plan-price">
        <span class="plan-price__crossed">{{
          (isSuperDisc ? (discountPrices.renewalPrice ?? basePrices.renewalPrice ?? basePrices.fullPrice) : basePrices.fullPrice)?.toFixed(2)
        }} {{ basePrices.currencyCode }}</span>
        <span>{{ discountPrices.discPrice?.toFixed(2) }} {{ basePrices.currencyCode }}</span>
      </p>
    </div>

    <div
      v-if="fullPricePerDay && discountPrices.discPricePerDay"
      class="price-perday"
    >
      <nuxt-icon name="price-bg" />
      <span class="price-perday__discount paragraph-12-600">{{
        fullPricePerDay?.toFixed(2)
      }} {{ basePrices.currencyCode }}</span>
      <span class="price-perday__full-price paragraph-12-600">{{
        discountPrices.discPricePerDay?.toFixed(2)
      }} {{ basePrices.currencyCode }}</span>
      <span class="price-perday__perday paragraph-12-600">{{ $t('lp_v7_plan_1month_per_day_text') }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';

const props = defineProps({
  planName: String,
  id: [String, Number],
  discountPrices: {
    type: Object as PropType<PaymentModel>,
    required: true,
  },
  basePrices: {
    type: Object as PropType<SubscriptionModel>,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  theme: String,
  isSuperDisc: {
    type: Boolean,
    default: false,
  },
  isMostPopular: {
    type: Boolean,
    default: false,
  },
});
const { locale } = useI18n();

const fullPricePerDay = computed(() => {
  if (props.isSuperDisc) {
    return props.discountPrices.fullPricePerDay ?? props.basePrices.fullPricePerDay;
  }

  return props.basePrices.fullPricePerDay;
});
</script>

<style lang="scss" scoped>
  .plan-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 72px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.60);
    width: 100%;
    padding: 8px 8px 8px 8px;
    position: relative;

    .most-popular {
      display: flex;
      align-items: center;
      position: absolute;
      left: 20px;
      top: -9px;
      height: 19px;
      background: $bg-dark-gray;
      border-radius: 50px;
      padding: 0 15px;

      &__text {
        text-transform: uppercase;
        color: rgba(17, 17, 19, 0.5);
      }
    }

    .nuxt-icon {
      font-size: 20px;
      line-height: 0;
    }

    .plan-name {
      font-family: $primary-fonts;
      line-height: 21px;
      color: rgba(17, 17, 19, 0.50);
      text-transform: uppercase;
      font-size: 16px;
      font-weight: 700 !important;

      &__small {
        font-size: 13px;
        white-space: nowrap;
        max-width: 120px;

        &.it {
          @media (max-width: 400px) {
            font-size: 12px;
          }
        }
      }
    }

    .plan-price {
      display: flex;
      gap: 4px;
      font-size: 12px;
      color: #767676;

      &__crossed {
        text-decoration: line-through;
      }
    }

    .text-wrapper {
      padding-left: 8px;
      flex: 1;
      min-width: 0;
    }

    .price-perday {
      display: flex;
      flex-direction: column;
      align-items: center;
      height: 100%;
      width: 112px;
      text-align: center;
      padding: 5px 8px 5px 20px;
      position: relative;
      margin-left: auto;
      background: transparent;

      :deep(.nuxt-icon) {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        color: $gray-bg;
        font-size: 112px;
        line-height: 0;
        z-index: 1;

        svg {
          width: 112px;
          height: 56px;
        }
      }

      &__discount {
        text-decoration: line-through;
        color: rgba(17, 17, 19, 0.25);
        margin-bottom: 2px;
        z-index: 2;
      }

      &__full-price {
        font-size: 16px;
        font-weight: 700;
        color: var(--theme-color);
        opacity: 0.5;
        z-index: 2;
      }

      &__perday {
        color: rgba(17, 17, 19, 0.50);
        z-index: 2;
      }
    }
  }

  .plan-item-selected {
    position: relative;
    border: 1px solid var(--theme-color);

    .most-popular {
      background: var(--theme-color);

      &__text {
        color: $white;
      }
    }

    .plan-name {
      color: $text-color;
    }

    .price-perday {
      .nuxt-icon {
        color: var(--theme-color);
      }

      &__discount {
        color: $text-color;
      }

      &__full-price,
      &__perday {
        color: $white;
        opacity: 1;
      }
    }

    :deep(>.nuxt-icon) {
      path {
        fill: var(--theme-color);
      }
    }
  }
</style>
